import { inject, NgModule } from '@angular/core';
import { NoPreloading, RouterModule, Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { AuthService } from './core/services/auth.service';


const routes: Routes = [{
  path: '',
  redirectTo: () => inject(AuthService).isLoggedIn().then(loggedIn => loggedIn ? '/home' : '/landing'),
  pathMatch: 'full'
}, {
  path: 'employment/respond',
  loadComponent: () => import('./features/invitations/employment-respond/employment-respond').then(m => m.EmploymentRespondComponent)
}, {
  path: 'register-invited',
  loadComponent: () => import('./features/invitations/register-invited/register-invited').then(m => m.RegisterInvitedComponent)
}, {
  path: 'onboarding',
  loadChildren: () => import('./features/onboarding/onboarding-module').then(m => m.OnboardingModule),
  canActivate: [authGuard]
}, {
  path: 'business-documents',
  loadChildren: () => import('./apps/document/document-module').then(m => m.DocumentModule),
  canActivate: [authGuard]
}, {
  path: 'doc',
  loadChildren: () => import('./docs/docs-module').then(m => m.DocsModule)
}, {
  path: 'docs',
  loadChildren: () => import('./docs/docs-module').then(m => m.DocsModule)
}, {
  path: 'sign/:token',
  loadComponent: () => import('./apps/contracts/public-sign/public-sign').then(m => m.PublicSign)
}, {
  path: 'out-steps/:token',
  loadComponent: () => import('./features/out-steps/out-steps/out-steps').then(m => m.OutSteps)
}, {
  path: 'landing',
  loadChildren: () => import('./landing/landing-module').then(m => m.LandingModule)
}, {
  path: 'home',
  loadChildren: () => import('./features/home/home-module').then(m => m.HomeModule),
  canActivate: [authGuard]
}, {
  path: 'user',
  loadChildren: () => import('./features/user/user-module').then(m => m.UserModule),
  canActivate: [authGuard]
}, {
  path: 'auth',
  loadChildren: () => import('./features/authentication/authentication-module').then(m => m.AuthenticationModule),
  // Login/register own their guest guards. Verification and recovery links
  // must also remain reachable with an existing (including unverified) session.
}, {
  path: 'configuration',
  loadChildren: () => import('./apps/configuration/configuration-module').then(m => m.ConfigurationModule),
  canActivate: [authGuard]
}, {
  path: 'documents',
  loadChildren: () => import('./apps/document/document-module').then(m => m.DocumentModule),
  canActivate: [authGuard]
}, {
  path: 'finance',
  loadChildren: () => import('./apps/finance/finance-module').then(m => m.FinanceModule),
  canActivate: [authGuard]
}, {
  path: 'invoicing',
  loadChildren: () => import('./apps/invoicing/invoicing-module').then(m => m.InvoicingModule),
  canActivate: [authGuard]
}, {
  path: 'hr',
  loadChildren: () => import('./apps/hr/hr-module').then(m => m.HrModule),
  canActivate: [authGuard]
}, {
  path: 'recruitment',
  loadChildren: () => import('./apps/recruitment/recruitment-module').then(m => m.RecruitmentModule),
  canActivate: [authGuard]
}, {
  path: 'payroll',
  loadChildren: () => import('./apps/payroll/payroll-module').then(m => m.PayrollModule),
  canActivate: [authGuard]
}, {
  path: 'attendance',
  loadChildren: () => import('./apps/attendance/attendance-module').then(m => m.AttendanceModule),
  canActivate: [authGuard]
}, {
  path: 'schedule-planning',
  loadChildren: () => import('./apps/schedule-planning/schedule-planning-module').then(m => m.SchedulePlanningModule),
  canActivate: [authGuard]
}, {
  path: 'crm',
  loadChildren: () => import('./apps/crm/crm-module').then(m => m.CrmModule),
  canActivate: [authGuard]
}, {
  path: 'inventory',
  loadChildren: () => import('./apps/inventory/inventory-module').then(m => m.InventoryModule),
  canActivate: [authGuard]
}, {
  path: 'maintenance',
  loadChildren: () => import('./apps/maintanance/maintanance-module').then(m => m.MaintananceModule),
  canActivate: [authGuard]
}, {
  path: 'maintanance',
  loadChildren: () => import('./apps/maintanance/maintanance-module').then(m => m.MaintananceModule),
  canActivate: [authGuard]
}, {
  path: 'procurement',
  loadChildren: () => import('./apps/procurement/procurement-module').then(m => m.ProcurementModule),
  canActivate: [authGuard]
}, {
  path: 'projects',
  loadChildren: () => import('./apps/project/project-module').then(m => m.ProjectModule),
  canActivate: [authGuard]
}, {
  path: 'project',
  loadChildren: () => import('./apps/project/project-module').then(m => m.ProjectModule),
  canActivate: [authGuard]
}, {
  path: 'sales',
  loadChildren: () => import('./apps/sales/sales-module').then(m => m.SalesModule),
  canActivate: [authGuard]
}, {
  path: 'manufacturing',
  loadChildren: () => import('./apps/manufacturing/manufacturing-module').then(m => m.ManufacturingModule),
  canActivate: [authGuard]
}, {
  path: 'mail',
  loadChildren: () => import('./personal/email-client/email-client-module').then(m => m.EmailClientModule),
  canActivate: [authGuard]
}, {
  path: 'contracts',
  loadChildren: () => import('./apps/contracts/contracts-module').then(m => m.ContractsModule),
  canActivate: [authGuard]
}, {
  path: 'integrations',
  loadChildren: () => import('./apps/integrations/integrations-module').then(m => m.IntegrationsModule),
  canActivate: [authGuard]
}, {
  path: 'help-desk',
  loadChildren: () => import('./apps/help-desk/help-desk-module').then(m => m.HelpDeskModule),
  canActivate: [authGuard]
}, {
  path: 'point-of-sale',
  loadChildren: () => import('./apps/point-of-sale/point-of-sale-module').then(m => m.PointOfSaleModule),
  canActivate: [authGuard]
}, {
  path: 'subscriptions',
  loadChildren: () => import('./apps/subscriptions/subscriptions-module').then(m => m.SubscriptionsModule),
  canActivate: [authGuard]
}, {
  path: 'rental',
  loadChildren: () => import('./apps/rental/rental-module').then(m => m.RentalModule),
  canActivate: [authGuard]
}, {
  path: 'expenses',
  loadChildren: () => import('./apps/expenses/expenses-module').then(m => m.ExpensesModule),
  canActivate: [authGuard]
}, {
  path: 'time-off',
  loadChildren: () => import('./apps/time-off/time-off-module').then(m => m.TimeOffModule),
  canActivate: [authGuard]
}, {
  path: 'timesheets',
  loadChildren: () => import('./apps/timesheets/timesheets-module').then(m => m.TimesheetsModule),
  canActivate: [authGuard]
}, {
  path: 'field-service',
  loadChildren: () => import('./apps/field-service/field-service-module').then(m => m.FieldServiceModule),
  canActivate: [authGuard]
}, {
  path: 'appointments',
  loadChildren: () => import('./apps/appointments/appointments-module').then(m => m.AppointmentsModule),
  canActivate: [authGuard]
}, {
  path: 'personal',
  loadChildren: () => import('./personal/personal-module').then(m => m.PersonalModule),
  canActivate: [authGuard]
}];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: NoPreloading })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
